import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Typography,
  Upload,
} from 'antd';
import { IconUpload } from '@tabler/icons-react';
import type { UploadProps } from 'antd';

import { programKindLabels, programKindValues } from '@/entities/training/model/librarySchema';

const { Text } = Typography;
const { TextArea } = Input;

const PROGRAM_OPTIONS = programKindValues.map((v) => ({
  label: programKindLabels[v],
  value: v,
}));

const DOC_TYPE_OPTIONS = [
  { value: 'certificate', label: 'Удостоверение' },
  { value: 'protocol', label: 'Протокол' },
  { value: 'diploma', label: 'Свидетельство' },
  { value: 'act', label: 'Акт' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  prefilledEmployee?: string;
  prefilledProgram?: string;
}

export function RecordTrainingDrawer({ open, onClose, prefilledEmployee, prefilledProgram }: Props) {
  const [form] = Form.useForm();
  const [result, setResult] = useState<'passed' | 'not_passed'>('passed');
  const [indefinite, setIndefinite] = useState(false);

  const uploadProps: UploadProps = {
    beforeUpload: () => false,
    accept: '.pdf,.jpg,.jpeg,.png',
    maxCount: 5,
  };

  const handleSubmit = () => {
    onClose();
  };

  return (
    <Drawer
      title="Фиксация прохождения обучения"
      open={open}
      onClose={onClose}
      width={560}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="primary" onClick={handleSubmit} disabled>
            Сохранить
          </Button>
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        message="Фиксация прохождения обучения будет доступна в следующей версии."
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical" disabled>
        <Form.Item label="Сотрудник" required>
          <Input placeholder="Поиск по ФИО / табельному" defaultValue={prefilledEmployee ?? ''} />
        </Form.Item>

        <Form.Item label="Программа обучения" required>
          <Select
            placeholder="Выбрать из реестра"
            options={PROGRAM_OPTIONS}
            defaultValue={prefilledProgram}
            allowClear
          />
        </Form.Item>

        <Form.Item label="Дата прохождения" required>
          <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} placeholder="Сегодня" />
        </Form.Item>

        <Form.Item label="Результат" required>
          <Radio.Group value={result} onChange={(e) => setResult(e.target.value)}>
            <Radio value="passed">Пройдено</Radio>
            <Radio value="not_passed">Не пройдено</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="Номер удостоверения / протокола">
          <Input placeholder="Номер документа" />
        </Form.Item>

        <Form.Item label="Кем выдано">
          <Input placeholder="Организация" />
        </Form.Item>

        <Form.Item label="Срок действия">
          <Space>
            <DatePicker format="DD.MM.YYYY" disabled={indefinite} placeholder="Автоматически" />
            <Checkbox checked={indefinite} onChange={(e) => setIndefinite(e.target.checked)}>
              Бессрочно
            </Checkbox>
          </Space>
        </Form.Item>

        <Form.Item label="Балл (опционально)">
          <Input type="number" placeholder="—" style={{ width: 120 }} />
        </Form.Item>

        <Form.Item label="Подтверждающие документы">
          <Upload.Dragger {...uploadProps}>
            <p>
              <IconUpload size={20} style={{ color: 'var(--n400)' }} />
            </p>
            <Text type="secondary" style={{ fontSize: 12 }}>
              PDF, JPG, PNG — до 20 МБ
            </Text>
          </Upload.Dragger>
          <Select placeholder="Тип документа" options={DOC_TYPE_OPTIONS} style={{ width: '100%', marginTop: 8 }} />
        </Form.Item>

        <Form.Item label="Комментарий">
          <TextArea rows={2} placeholder="Попадёт в комментарии деталки" />
        </Form.Item>

        <Space direction="vertical" size={0}>
          <Checkbox defaultChecked>Уведомить сотрудника</Checkbox>
          <Checkbox>Уведомить руководителя</Checkbox>
        </Space>
      </Form>
    </Drawer>
  );
}
